import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage, setSelectedStudent } from "../../redux/admin/student.slice";
import { fetchStudents, addStudent, updateStudent } from "../../redux/admin/student.thunk";
import { fetchFeeMasters } from "../../redux/admin/fee_master.thunk";
import {
  fetchStudentActiveFeePlan,
  fetchStudentFeePlanHistory,
  saveStudentFeePlan,
  updateStudentFeePlanThunk,
} from "../../redux/admin/student_fee_plan.thunk";
import { selectFeeMasters } from "../../redux/admin/fee_master.selector";
import {
  selectActiveFeePlan,
  selectFeePlanHistory,
  selectFeePlanLoading,
} from "../../redux/admin/student_fee_plan.selector";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* =========================================================================
   STATIC CONFIG (kept outside the component so it is not re-created per render)
   ========================================================================= */

// Quick-view field sequences. Reorder / add / remove keys freely.
// Overview tab (most important details first)
const STUDENT_VIEW_PRIMARY_FIELDS = ["name", "parents", "contact", "email", "address"];
// Player IDs tab
const STUDENT_VIEW_PLAYER_FIELDS = ["tnsca_id", "dca_id", "fide_id", "aicf_id", "lichess_id"];
// Used by "Copy all details"
const STUDENT_VIEW_FIELD_ORDER = [...STUDENT_VIEW_PRIMARY_FIELDS, ...STUDENT_VIEW_PLAYER_FIELDS];

const PAGE_SIZE = 10;

const STATES = ["Tamil Nadu", "Kerala", "Karnataka", "Maharashtra"];

const DISTRICTS_MAP = {
  "Tamil Nadu": [
    "Ariyalur", "Chengalpattu", "Chennai", "Coimbatore", "Cuddalore",
    "Dharmapuri", "Dindigul", "Erode", "Kallakurichi", "Kancheepuram",
    "Kanniyakumari", "Karur", "Krishnagiri", "Madurai", "Mayiladuthurai",
    "Nagapattinam", "Namakkal", "Nilgiris", "Perambalur", "Pudukkottai",
    "Ramanathapuram", "Ranipet", "Salem", "Sivaganga", "Tenkasi",
    "Thanjavur", "Theni", "Thoothukudi", "Tiruchirappalli", "Tirunelveli",
    "Tirupathur", "Tiruppur", "Tiruvallur", "Tiruvannamalai", "Tiruvarur",
    "Vellore", "Viluppuram", "Virudhunagar",
  ],
  Kerala: ["Kochi", "Thiruvananthapuram", "Kozhikode"],
  Karnataka: ["Bengaluru", "Mysuru", "Mangaluru"],
  Maharashtra: ["Mumbai", "Pune", "Nagpur"],
};

/* =========================================================================
   HELPERS
   ========================================================================= */

const todayISO = () => new Date().toISOString().split("T")[0];

// Date inputs only accept YYYY-MM-DD; APIs often return full ISO timestamps.
const toDateInput = (value) => (value ? String(value).split("T")[0] : "");

const getErrorMessage = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  return err.message || err.error || fallback;
};

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    /* fall through to legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
};

const formatDays = (days) => {
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  const rem = (days % 365) % 30;
  const parts = [];
  if (years) parts.push(`${years} yr`);
  if (months) parts.push(`${months} mo`);
  if (!years && !months) parts.push(`${rem} day${rem === 1 ? "" : "s"}`);
  return parts.join(" ");
};

// "2024-05-01" -> "01 May 2024" (parsed as a local date to avoid timezone shifts)
const formatDisplayDate = (value) => {
  const v = toDateInput(value);
  if (!v) return "—";
  const [y, m, d] = v.split("-").map(Number);
  if (!y || !m || !d) return v;
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const getEnrollmentMeta = (en) => {
  if (!en.doj) return { status: "draft", label: "Incomplete", duration: "—" };
  const start = new Date(en.doj);
  const end = en.leaving_date ? new Date(en.leaving_date) : new Date();
  const days = Math.max(0, Math.floor((end - start) / 86400000));
  return {
    status: en.leaving_date ? "completed" : "active",
    label: en.leaving_date ? "Completed" : "Currently Active",
    duration: formatDays(days),
  };
};

// Condenses a student's enrollment periods into joined / left / rejoin facts.
const getEnrollmentSummary = (enrollments) => {
  const list = (enrollments || [])
    .filter((e) => e && e.doj)
    .map((e) => ({ ...e, doj: toDateInput(e.doj), leaving_date: toDateInput(e.leaving_date) }))
    .sort((a, b) => a.doj.localeCompare(b.doj));
  if (!list.length) return null;

  const open = [...list].reverse().find((e) => !e.leaving_date) || null;
  const leftDates = list.map((e) => e.leaving_date).filter(Boolean).sort();
  const rejoins = list.length - 1;
  const totalDays = list.reduce((sum, e) => {
    const start = new Date(e.doj);
    const end = e.leaving_date ? new Date(e.leaving_date) : new Date();
    return sum + Math.max(0, Math.floor((end - start) / 86400000));
  }, 0);

  return {
    list,
    firstJoined: list[0].doj,
    isActive: Boolean(open),
    currentSince: open ? open.doj : null,
    lastLeft: open ? null : leftDates[leftDates.length - 1] || null,
    prevLeft: leftDates[leftDates.length - 1] || null,
    rejoins,
    lastRejoin: rejoins > 0 ? list[list.length - 1].doj : null,
    tenure: formatDays(totalDays),
  };
};

// Windowed page list: 1 … 4 5 [6] 7 8 … 20
const getPageWindow = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push(`gap-${p}`);
    out.push(p);
  });
  return out;
};

const isEmptyViewValue = (v) => !v || v === "N/A" || v === "N/A / N/A";

const renderViewFieldContent = (key, st) => {
  switch (key) {
    case "name":
      return { label: "Full Name", icon: "fa-user", wide: true, value: st.full_name || `${st.name || ""} ${st.last_name || ""}`.trim() || "N/A" };
    case "parents":
      return { label: "Father / Mother", icon: "fa-users", wide: true, value: `${st.father_name || "N/A"} / ${st.mother_name || "N/A"}` };
    case "contact":
      return { label: "Mobile", icon: "fa-phone", value: st.mobile_no || st.alternative_no || "N/A" };
    case "email":
      return { label: "Email", icon: "fa-envelope", value: st.email || "N/A" };
    case "address": {
      const fullAddr = [st.address, st.address2, st.city, st.district, st.state, st.pincode]
        .filter(Boolean)
        .join(", ");
      return { label: "Address", icon: "fa-map-marker-alt", wide: true, value: fullAddr || "N/A" };
    }
    case "tnsca_id":
      return { label: "TNSCA ID", icon: "fa-chess-rook", value: st.tnsca_id || "N/A" };
    case "dca_id":
      return { label: "DCA ID", icon: "fa-chess-bishop", value: st.dca_id || "N/A" };
    case "fide_id":
      return { label: "FIDE ID", icon: "fa-chess-king", value: st.fide_id || "N/A" };
    case "aicf_id":
      return { label: "AICF ID", icon: "fa-chess-knight", value: st.aicf_id || "N/A" };
    case "lichess_id":
      return { label: "Lichess ID", icon: "fa-chess", value: st.lichess_id || "N/A" };
    default:
      return null;
  }
};

const emptyFeeForm = () => ({
  fee_master_id: "",
  amount: "",
  billing_cycle: "monthly",
  discount: "0.00",
  effective_from: todayISO(),
});

const newEnrollmentRow = (remarks = "Initial Admission") => ({
  id: null,
  doj: todayISO(),
  leaving_date: "",
  remarks,
});

const INITIAL_FORM_STATE = {
  admission_number: "", name: "", last_name: "", dob: "", gender: "M",
  aadhaar_no: "", father_name: "", mother_name: "", guardian_name: "",
  mother_tongue: "", father_occupation: "", mother_occupation: "",
  mobile_no: "", alternative_no: "", email: "", address: "",
  address2: "", state: "", district: "", city: "", pincode: "",
  school: "", grade: "", status: "active",
  dca_id: "", tnsca_id: "", aicf_id: "", fide_id: "",
  lichess_id: "", lichess_password: "",
  enrollments: [],
};

/* =========================================================================
   COMPONENT
   ========================================================================= */

const Students = () => {
  const dispatch = useDispatch();
  const limit = PAGE_SIZE;

  const {
    students = [],
    totalPages = 1,
    currentPage = 1,
    total = 0,
    loading = false,
    selectedStudent = null,
  } = useSelector((state) => state.admin?.students || {});

  const feeMasters = useSelector(selectFeeMasters) || [];
  const activeFeePlan = useSelector(selectActiveFeePlan);
  const feeHistory = useSelector(selectFeePlanHistory) || [];
  const feePlanLoading = useSelector(selectFeePlanLoading);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Quick view
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewStudentData, setViewStudentData] = useState(null);
  const [viewTab, setViewTab] = useState("overview");

  // Enrollment history popup (opened from the list)
  const [historyStudent, setHistoryStudent] = useState(null);

  // Fee plan
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [feeStudent, setFeeStudent] = useState(null);
  const [isHistoryView, setIsHistoryView] = useState(false);
  const [editingFeeRecord, setEditingFeeRecord] = useState(null);
  const [feeSubmitting, setFeeSubmitting] = useState(false);
  const [feeFormData, setFeeFormData] = useState(emptyFeeForm());

  const [formData, setFormData] = useState(INITIAL_FORM_STATE);

  /* ----------------------------- effects ----------------------------- */

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    dispatch(fetchStudents({ page: currentPage, limit, search: debouncedSearch, status: statusFilter }));
  }, [dispatch, currentPage, limit, debouncedSearch, statusFilter]);

  useEffect(() => {
    if (editingFeeRecord) return;
    if (activeFeePlan) {
      setFeeFormData({
        fee_master_id: activeFeePlan.fee_master_id || "",
        amount: activeFeePlan.amount || "",
        billing_cycle: activeFeePlan.billing_cycle || "monthly",
        discount: activeFeePlan.discount || "0.00",
        effective_from: toDateInput(activeFeePlan.effective_from) || todayISO(),
      });
    } else {
      setFeeFormData(emptyFeeForm());
    }
  }, [activeFeePlan, editingFeeRecord]);

  // Close the open modal with Escape.
  useEffect(() => {
    if (!showModal && !showViewModal && !showFeeModal && !historyStudent) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (historyStudent) setHistoryStudent(null);
      else if (showFeeModal) setShowFeeModal(false);
      else if (showViewModal) setShowViewModal(false);
      else if (showModal) setShowModal(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showModal, showViewModal, showFeeModal, historyStudent]);

  // Lock background scroll while a modal is open.
  useEffect(() => {
    const anyOpen = showModal || showViewModal || showFeeModal || Boolean(historyStudent);
    const prev = document.body.style.overflow;
    if (anyOpen) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showModal, showViewModal, showFeeModal, historyStudent]);

  /* ----------------------------- handlers ----------------------------- */

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) dispatch(setCurrentPage(newPage));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    if (currentPage !== 1) dispatch(setCurrentPage(1));
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    dispatch(setCurrentPage(1));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? "active" : "inactive") : value,
      ...(name === "state" ? { district: "", city: "" } : {}),
    }));
  };

  const handleEnrollmentChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...(prev.enrollments || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, enrollments: updated };
    });
  };

  const handleAddNewEnrollmentRow = () => {
    setFormData((prev) => {
      const hasHistory = (prev.enrollments || []).length > 0;
      return {
        ...prev,
        enrollments: [...(prev.enrollments || []), newEnrollmentRow(hasHistory ? "Rejoined" : "Initial Admission")],
      };
    });
  };

  const handleRemoveEnrollmentRow = (index) => {
    setFormData((prev) => ({
      ...prev,
      enrollments: (prev.enrollments || []).filter((_, i) => i !== index),
    }));
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setFormData({ ...INITIAL_FORM_STATE, enrollments: [newEnrollmentRow()] });
    setActiveTab("basic");
    setShowPassword(false);
    setShowModal(true);
  };

  const openEditModal = (student) => {
    setIsEditMode(true);
    dispatch(setSelectedStudent(student));

    const fetchedPassword = student.lichess_password || student.Lichess_password || "";

    setFormData({
      ...INITIAL_FORM_STATE,
      ...student,
      dob: toDateInput(student.dob),
      status: student.status || "active",
      lichess_password: fetchedPassword,
      enrollments: (student.enrollments || []).map((en) => ({
        ...en,
        doj: toDateInput(en.doj),
        leaving_date: toDateInput(en.leaving_date),
      })),
    });
    setActiveTab("basic");
    setShowPassword(false);
    setShowModal(true);
  };

  const openViewModal = (student) => {
    setViewStudentData(student);
    setViewTab("overview");
    setShowViewModal(true);
  };

  const openFeeModal = (student) => {
    setFeeStudent(student);
    setShowFeeModal(true);
    setIsHistoryView(false);
    setEditingFeeRecord(null);

    dispatch(fetchFeeMasters());
    dispatch(fetchStudentActiveFeePlan(student.id));
    dispatch(fetchStudentFeePlanHistory(student.id));
  };

  const handleEditFeeRecord = (record) => {
    setEditingFeeRecord(record);
    setFeeFormData({
      fee_master_id: record.fee_master_id || "",
      amount: record.amount || "",
      billing_cycle: record.billing_cycle || "monthly",
      discount: record.discount || "0.00",
      effective_from: toDateInput(record.effective_from) || todayISO(),
    });
    setIsHistoryView(false);
  };

  const handleFeeMasterChange = (e) => {
    const masterId = e.target.value;
    const selectedMaster = feeMasters.find((m) => String(m.id) === String(masterId));

    if (selectedMaster) {
      setFeeFormData((prev) => ({
        ...prev,
        fee_master_id: masterId,
        amount: selectedMaster.amount,
        billing_cycle: selectedMaster.billing_cycle,
      }));
    } else {
      setFeeFormData((prev) => ({ ...prev, fee_master_id: masterId }));
    }
  };

  const handleFeeFieldChange = (field) => (e) =>
    setFeeFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleFeeSubmit = async (e) => {
    e.preventDefault();
    if (feeSubmitting) return;

    if (!feeFormData.amount || !feeFormData.effective_from) {
      toastr.warning("Amount and Effective From date are required");
      return;
    }
    if (Number(feeFormData.amount) <= 0) {
      toastr.warning("Base fee amount must be greater than zero");
      return;
    }
    if (Number(feeFormData.discount || 0) < 0 || Number(feeFormData.discount || 0) > Number(feeFormData.amount)) {
      toastr.warning("Discount must be between 0 and the base fee amount");
      return;
    }

    setFeeSubmitting(true);
    try {
      const payload = {
        student_id: feeStudent.id,
        ...(editingFeeRecord ? { record_id: editingFeeRecord.id } : {}),
        ...feeFormData,
      };

      const actionToDispatch = editingFeeRecord
        ? updateStudentFeePlanThunk(payload)
        : saveStudentFeePlan(payload);

      const resultAction = await dispatch(actionToDispatch);
      const failed = Boolean(resultAction.error) || resultAction.meta?.requestStatus === "rejected";

      if (!failed) {
        toastr.success(
          editingFeeRecord ? "Fee entry corrected successfully" : "Fee plan saved successfully"
        );
        setEditingFeeRecord(null);
        setShowFeeModal(false);
        dispatch(fetchStudentFeePlanHistory(feeStudent.id));
        dispatch(fetchStudentActiveFeePlan(feeStudent.id));
      } else {
        toastr.error(getErrorMessage(resultAction.payload || resultAction.error, "Failed to save fee plan"));
      }
    } catch (err) {
      toastr.error("Could not reach the server while saving the fee plan");
    } finally {
      setFeeSubmitting(false);
    }
  };

  const validateEnrollments = (enrollments = []) => {
    for (let i = 0; i < enrollments.length; i += 1) {
      const en = enrollments[i];
      if (!en.doj) return `Period ${i + 1}: Date of Joining is required`;
      if (en.leaving_date && new Date(en.leaving_date) < new Date(en.doj)) {
        return `Period ${i + 1}: Leaving date cannot be earlier than the joining date`;
      }
    }
    const openPeriods = enrollments.filter((en) => !en.leaving_date).length;
    if (openPeriods > 1) {
      return "Only one enrollment period can be open (without a leaving date)";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (submitting) return;

    if (!formData.name || !formData.mobile_no || !formData.admission_number) {
      toastr.warning("Admission number, first name and mobile number are required");
      return;
    }

    const enrollmentError = validateEnrollments(formData.enrollments);
    if (enrollmentError) {
      setActiveTab("tracking");
      toastr.warning(enrollmentError);
      return;
    }

    setSubmitting(true);
    try {
      const payload = { ...formData };

      if (isEditMode && !payload.lichess_password) {
        delete payload.lichess_password;
      }

      if (isEditMode) {
        const studentId = formData.id || selectedStudent?.id;
        await dispatch(updateStudent({ id: studentId, studentData: payload })).unwrap();
        toastr.success("Profile and enrollment saved");
      } else {
        await dispatch(addStudent(payload)).unwrap();
        toastr.success("Admission completed");
      }
      setShowModal(false);
      dispatch(fetchStudents({ page: currentPage, limit, search: debouncedSearch, status: statusFilter }));
    } catch (err) {
      toastr.error(getErrorMessage(err, "Could not save the student record"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyField = async (fieldInfo) => {
    const ok = await copyToClipboard(fieldInfo.value);
    if (ok) toastr.success(`Copied ${fieldInfo.label}`);
    else toastr.error("Copy failed. Please select and copy manually.");
  };

  const handleCopyAll = async () => {
    if (!viewStudentData) return;
    const lines = STUDENT_VIEW_FIELD_ORDER
      .map((k) => renderViewFieldContent(k, viewStudentData))
      .filter((f) => f && !isEmptyViewValue(f.value))
      .map((f) => `${f.label}: ${f.value}`);
    const summary = getEnrollmentSummary(viewStudentData.enrollments);
    if (summary) {
      lines.push(`Joined: ${formatDisplayDate(summary.firstJoined)}`);
      lines.push(`Left: ${summary.isActive ? "Currently active" : formatDisplayDate(summary.lastLeft)}`);
      if (summary.rejoins > 0) {
        lines.push(`Rejoined: ${summary.rejoins}x (last on ${formatDisplayDate(summary.lastRejoin)})`);
      }
    }
    const text = lines.join("\n");
    const ok = await copyToClipboard(text);
    if (ok) toastr.success("All details copied");
    else toastr.error("Copy failed. Please select and copy manually.");
  };

  /* ----------------------------- render helpers ----------------------------- */

  const renderPagination = () =>
    getPageWindow(currentPage, totalPages).map((p) =>
      typeof p === "string" ? (
        <span key={p} className="mnc-page-gap">…</span>
      ) : (
        <button
          key={p}
          type="button"
          onClick={() => handlePageChange(p)}
          className={`mnc-page-num ${currentPage === p ? "active" : ""}`}
          aria-current={currentPage === p ? "page" : undefined}
        >
          {p}
        </button>
      )
    );

  const renderEnrollmentCell = (st) => {
    const sum = getEnrollmentSummary(st.enrollments);
    if (!sum) return <span className="mnc-enr-none">No enrollment record</span>;
    return (
      <div className="mnc-enr-cell">
        <div className="mnc-enr-row">
          <span className="mnc-enr-key">Joined</span>
          <span className="mnc-enr-val">{formatDisplayDate(sum.firstJoined)}</span>
        </div>
        <div className="mnc-enr-row">
          <span className="mnc-enr-key">Left</span>
          {sum.isActive ? (
            <>
              <span className="mnc-enr-val is-active">Present</span>
              {sum.prevLeft && <span className="mnc-enr-prev">prev. left {formatDisplayDate(sum.prevLeft)}</span>}
            </>
          ) : (
            <span className="mnc-enr-val is-left">{formatDisplayDate(sum.lastLeft)}</span>
          )}
        </div>
        <div className="mnc-enr-row">
          <span className="mnc-enr-key">Rejoin</span>
          {sum.rejoins > 0 ? (
            <span className="mnc-enr-val">
              <span className="mnc-enr-rejoin">{sum.rejoins}×</span>
              {formatDisplayDate(sum.lastRejoin)}
            </span>
          ) : (
            <span className="mnc-enr-val muted">—</span>
          )}
        </div>
        <button type="button" className="mnc-enr-history" onClick={() => setHistoryStudent(st)}>
          <i className="fas fa-stream"></i> View history
        </button>
      </div>
    );
  };

  // Shared read-only timeline: First joining / Rejoin N, with the break between periods.
  const renderPeriodTimeline = (summary) => (
    <div className="mnc-vt">
      {summary.list.map((en, index) => {
        const meta = getEnrollmentMeta(en);
        const prev = index > 0 ? summary.list[index - 1] : null;
        const gapDays =
          prev && prev.leaving_date
            ? Math.max(0, Math.floor((new Date(en.doj) - new Date(prev.leaving_date)) / 86400000))
            : null;
        return (
          <React.Fragment key={en.id ?? `p-${index}`}>
            {gapDays !== null && (
              <div className="mnc-vt-gap">
                <i className="far fa-pause-circle me-1"></i>Break of {formatDays(gapDays)}
              </div>
            )}
            <div className={`mnc-vt-item is-${meta.status}`}>
              <span className="mnc-vt-dot"></span>
              <div className="mnc-vt-head">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="mnc-period-index">{index === 0 ? "First joining" : `Rejoin ${index}`}</span>
                  <span className={`mnc-period-badge ${meta.status}`}>{meta.label}</span>
                </div>
                <span className="mnc-period-duration"><i className="far fa-clock me-1"></i>{meta.duration}</span>
              </div>
              <div className="mnc-vt-dates">
                <span>{formatDisplayDate(en.doj)}</span>
                <i className="fas fa-long-arrow-alt-right mnc-vt-arrow"></i>
                <span>{en.leaving_date ? formatDisplayDate(en.leaving_date) : "Present"}</span>
              </div>
              {en.remarks && <div className="mnc-vt-remarks">{en.remarks}</div>}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  const renderViewTiles = (keys) =>
    keys.map((fieldKey) => {
      const fieldInfo = renderViewFieldContent(fieldKey, viewStudentData);
      if (!fieldInfo) return null;
      const empty = isEmptyViewValue(fieldInfo.value);
      return (
        <div key={fieldKey} className={`mnc-view-tile ${fieldInfo.wide ? "wide" : ""}`}>
          <div className="mnc-view-icon"><i className={`fas ${fieldInfo.icon}`}></i></div>
          <div className="mnc-view-text">
            <span className="mnc-view-label">{fieldInfo.label}</span>
            <span className={`mnc-view-value ${empty ? "is-empty" : ""}`}>{empty ? "Not provided" : fieldInfo.value}</span>
          </div>
          {!empty && (
            <button
              type="button"
              className="mnc-view-copy"
              title={`Copy ${fieldInfo.label}`}
              aria-label={`Copy ${fieldInfo.label}`}
              onClick={() => handleCopyField(fieldInfo)}
            >
              <i className="far fa-copy"></i>
            </button>
          )}
        </div>
      );
    });

  const viewSummary = viewStudentData ? getEnrollmentSummary(viewStudentData.enrollments) : null;
  const historySummary = historyStudent ? getEnrollmentSummary(historyStudent.enrollments) : null;

  const dcaLabel = formData.district ? `${formData.district} DCA ID` : "DCA ID (District Chess Association)";

  /* ----------------------------- JSX ----------------------------- */

  return (
    <div className="mnc-page-bg">
      <div className="container-fluid px-4 py-4">
        {/* HEADER */}
        <div className="mnc-header-row d-flex justify-content-between align-items-end mb-4">
          <div>
            <h3 className="fw-800 text-dark mb-1">Student Registry</h3>
            <div className="d-flex align-items-center gap-2">
              <span className="badge-total shadow-sm">Total Records: {total}</span>
              <span className="text-muted small fw-600">| Enterprise Edition v4.6</span>
            </div>
          </div>
          <button type="button" className="btn btn-mnc-primary shadow-sm" onClick={openAddModal}>
            <i className="fas fa-plus-circle me-2"></i>New Admission
          </button>
        </div>

        {/* TOOLBAR */}
        <div className="mnc-toolbar shadow-sm mb-4">
          <div className="mnc-search-group">
            <i className="fas fa-search mnc-search-icon"></i>
            <input
              type="text"
              className="mnc-search-input"
              placeholder="Search by student name, ID, or mobile..."
              aria-label="Search students"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button
                type="button"
                className="mnc-clear-search"
                aria-label="Clear search"
                onClick={() => { setSearchTerm(""); dispatch(setCurrentPage(1)); }}
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>

          <div className="mnc-filter-group">
            <div className="mnc-filter-label-wrap">
              <i className="fas fa-filter text-muted x-small"></i>
              <span className="mnc-filter-caption">Status</span>
            </div>
            <select
              className="mnc-toolbar-select"
              aria-label="Filter by status"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <option value="active">Active Registry</option>
              <option value="inactive">Inactive Registry</option>
              <option value="">Complete Directory</option>
            </select>
          </div>
        </div>

        {/* TABLE */}
        <div className="mnc-card shadow-sm">
          <div className="table-responsive">
            <table className="table align-middle mb-0 mnc-table">
              <thead className="mnc-thead">
                <tr>
                  <th className="ps-4">STUDENT IDENTIFICATION</th>
                  <th>ADMISSION ID</th>
                  <th>GRADE</th>
                  <th>ENROLLMENT</th>
                  <th>STATUS</th>
                  <th className="text-end pe-4">MANAGEMENT</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="spinner-border text-primary spinner-border-sm" role="status" aria-label="Loading"></div>
                    </td>
                  </tr>
                ) : students.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5 text-muted fw-600 small">
                      No records found for this search.
                    </td>
                  </tr>
                ) : (
                  students.map((st) => (
                    <tr key={st.id} className="mnc-row">
                      <td className="ps-4" data-label="Student">
                        <div className="d-flex align-items-center">
                          {st.avatar || st.image ? (
                            <img src={st.avatar || st.image} alt="" className="mnc-avatar-img" />
                          ) : (
                            <div className={`mnc-avatar-mini ${st.status === "active" ? "active" : "inactive"}`}>
                              {st.name?.[0]}
                            </div>
                          )}
                          <div className="ms-3">
                            <div className="fw-700 text-dark small">
                              {st.full_name || `${st.name || ""} ${st.last_name || ""}`.trim()}
                            </div>
                            <div className="text-muted x-small">{st.email || st.mobile_no}</div>
                          </div>
                        </div>
                      </td>
                      <td data-label="Admission ID"><span className="mnc-id-tag">{st.admission_number}</span></td>
                      <td data-label="Grade"><span className="fw-700 text-secondary small">{st.grade || "N/A"}</span></td>
                      <td data-label="Enrollment">{renderEnrollmentCell(st)}</td>
                      <td data-label="Status">
                        <span className={`mnc-status-pill ${st.status === "active" ? "active" : "inactive"}`}>
                          {st.status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-end pe-4" data-label="Management">
                        <div className="d-flex justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn-mnc-outline-view"
                            title="Quick view details"
                            aria-label={`View details of ${st.name}`}
                            onClick={() => openViewModal(st)}
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            type="button"
                            className="btn-mnc-outline-fee"
                            title="Assign or modify fee plan"
                            aria-label={`Fee plan of ${st.name}`}
                            onClick={() => openFeeModal(st)}
                          >
                            <i className="fas fa-rupee-sign"></i>
                          </button>
                          <button type="button" className="btn-mnc-outline" onClick={() => openEditModal(st)}>
                            Edit Profile
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          <div className="mnc-footer-pagination">
            <div className="text-muted x-small fw-700 uppercase">Showing {students.length} of {total} records</div>
            <div className="mnc-pagination-hub">
              <button
                type="button"
                className="mnc-page-arrow"
                aria-label="Previous page"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              {renderPagination()}
              <button
                type="button"
                className="mnc-page-arrow"
                aria-label="Next page"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ================= QUICK VIEW MODAL ================= */}
      {showViewModal && viewStudentData && (
        <div
          className="mnc-modal-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowViewModal(false); }}
        >
          <div className="mnc-modal-window mnc-view-window" role="dialog" aria-modal="true" aria-label="Student details">
            <div className="mnc-modal-main w-100">
              <div className="mnc-view-hero">
                <button type="button" className="mnc-view-close" aria-label="Close" onClick={() => setShowViewModal(false)}>
                  <i className="fas fa-times"></i>
                </button>
                {viewStudentData.avatar || viewStudentData.image ? (
                  <img src={viewStudentData.avatar || viewStudentData.image} alt="" className="mnc-view-avatar" />
                ) : (
                  <div className={`mnc-view-avatar fallback ${viewStudentData.status === "active" ? "active" : "inactive"}`}>
                    {viewStudentData.name?.[0]}
                  </div>
                )}
                <div className="mnc-view-hero-text">
                  <h5 className="mnc-view-name">
                    {viewStudentData.full_name || `${viewStudentData.name || ""} ${viewStudentData.last_name || ""}`.trim()}
                  </h5>
                  <div className="mnc-view-chips">
                    <span className="mnc-view-chip mono">{viewStudentData.admission_number}</span>
                    <span className="mnc-view-chip">Grade {viewStudentData.grade || "N/A"}</span>
                    <span className={`mnc-view-chip status ${viewStudentData.status === "active" ? "active" : "inactive"}`}>
                      {viewStudentData.status === "active" ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mnc-view-tabs" role="tablist">
                {[
                  { id: "overview", label: "Overview", icon: "fa-th-large" },
                  { id: "enrollment", label: "Enrollment", icon: "fa-user-clock", count: viewSummary ? viewSummary.list.length : 0 },
                  { id: "player", label: "Player IDs", icon: "fa-chess-knight" },
                ].map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={viewTab === t.id}
                    className={`mnc-view-tab ${viewTab === t.id ? "active" : ""}`}
                    onClick={() => setViewTab(t.id)}
                  >
                    <i className={`fas ${t.icon}`}></i>
                    {t.label}
                    {t.count > 0 && <span className="mnc-view-tab-count">{t.count}</span>}
                  </button>
                ))}
              </div>

              <div className="mnc-modal-scroller mnc-view-body">
                {viewTab === "overview" && (
                  <>
                    <div className="mnc-view-stats">
                      <div className="mnc-view-stat joined">
                        <span className="mnc-view-stat-label">Joined</span>
                        <span className="mnc-view-stat-value">{viewSummary ? formatDisplayDate(viewSummary.firstJoined) : "—"}</span>
                        <span className="mnc-view-stat-sub">Date of joining</span>
                      </div>
                      <div className={`mnc-view-stat left ${viewSummary?.isActive ? "is-active" : ""}`}>
                        <span className="mnc-view-stat-label">Left</span>
                        <span className="mnc-view-stat-value">
                          {!viewSummary ? "—" : viewSummary.isActive ? "Present" : formatDisplayDate(viewSummary.lastLeft)}
                        </span>
                        <span className="mnc-view-stat-sub">
                          {viewSummary?.isActive ? `Active since ${formatDisplayDate(viewSummary.currentSince)}` : "Leaving date"}
                        </span>
                      </div>
                      <div className="mnc-view-stat rejoin">
                        <span className="mnc-view-stat-label">Rejoin</span>
                        <span className="mnc-view-stat-value">{viewSummary && viewSummary.rejoins > 0 ? `${viewSummary.rejoins}×` : "None"}</span>
                        <span className="mnc-view-stat-sub">
                          {viewSummary && viewSummary.rejoins > 0 ? `Last on ${formatDisplayDate(viewSummary.lastRejoin)}` : "Never rejoined"}
                        </span>
                      </div>
                      <div className="mnc-view-stat tenure">
                        <span className="mnc-view-stat-label">Tenure</span>
                        <span className="mnc-view-stat-value">{viewSummary ? viewSummary.tenure : "—"}</span>
                        <span className="mnc-view-stat-sub">Total time enrolled</span>
                      </div>
                    </div>
                    <div className="mnc-view-grid">{renderViewTiles(STUDENT_VIEW_PRIMARY_FIELDS)}</div>
                  </>
                )}

                {viewTab === "enrollment" && (
                  <>
                    {!viewSummary ? (
                      <div className="mnc-track-empty">
                        <i className="fas fa-calendar-plus"></i>
                        <div className="fw-700">No enrollment periods recorded</div>
                        <div className="x-small">Add joining and leaving dates from Edit Profile.</div>
                      </div>
                    ) : (
                      renderPeriodTimeline(viewSummary)
                    )}
                    <div className="mt-3">
                      <button
                        type="button"
                        className="btn-mnc-outline"
                        onClick={() => { setShowViewModal(false); openEditModal(viewStudentData); setActiveTab("tracking"); }}
                      >
                        <i className="fas fa-pen me-2"></i>Edit periods
                      </button>
                    </div>
                  </>
                )}

                {viewTab === "player" && (
                  <div className="mnc-view-grid">{renderViewTiles(STUDENT_VIEW_PLAYER_FIELDS)}</div>
                )}
              </div>

              <div className="mnc-view-footer">
                <button type="button" className="mnc-view-copyall" onClick={handleCopyAll}>
                  <i className="far fa-copy me-2"></i>Copy all details
                </button>
                <div className="mnc-view-actions">
                  <button
                    type="button"
                    className="btn-mnc-outline-fee-text"
                    onClick={() => { setShowViewModal(false); openFeeModal(viewStudentData); }}
                  >
                    <i className="fas fa-rupee-sign me-2"></i>Fee plan
                  </button>
                  <button
                    type="button"
                    className="btn-mnc-sync"
                    onClick={() => { setShowViewModal(false); openEditModal(viewStudentData); }}
                  >
                    Edit profile
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= ENROLLMENT HISTORY POPUP ================= */}
      {historyStudent && (
        <div
          className="mnc-modal-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setHistoryStudent(null); }}
        >
          <div className="mnc-modal-window mnc-hist-window" role="dialog" aria-modal="true" aria-label="Enrollment history">
            <div className="mnc-modal-main w-100">
              <div className="mnc-hist-head">
                <button type="button" className="mnc-view-close" aria-label="Close" onClick={() => setHistoryStudent(null)}>
                  <i className="fas fa-times"></i>
                </button>
                <div className="mnc-hist-title">Enrollment history</div>
                <div className="mnc-hist-sub">
                  {historyStudent.full_name || `${historyStudent.name || ""} ${historyStudent.last_name || ""}`.trim()} · {historyStudent.admission_number}
                </div>
              </div>

              {historySummary && (
                <div className="mnc-hist-summary">
                  <span className={`mnc-hist-chip ${historySummary.isActive ? "active" : ""}`}>
                    {historySummary.isActive ? "Currently active" : "Left"}
                  </span>
                  <span className="mnc-hist-chip">Tenure {historySummary.tenure}</span>
                  <span className="mnc-hist-chip rejoin">
                    {historySummary.rejoins > 0 ? `${historySummary.rejoins} rejoin${historySummary.rejoins > 1 ? "s" : ""}` : "No rejoin"}
                  </span>
                </div>
              )}

              <div className="mnc-modal-scroller mnc-hist-body">
                {historySummary ? (
                  renderPeriodTimeline(historySummary)
                ) : (
                  <div className="mnc-track-empty">
                    <i className="fas fa-calendar-plus"></i>
                    <div className="fw-700">No enrollment periods recorded</div>
                    <div className="x-small">Add joining and leaving dates from Edit Profile.</div>
                  </div>
                )}
              </div>

              <div className="mnc-view-footer">
                <button
                  type="button"
                  className="btn-mnc-outline"
                  onClick={() => { const st = historyStudent; setHistoryStudent(null); openEditModal(st); setActiveTab("tracking"); }}
                >
                  <i className="fas fa-pen me-2"></i>Edit periods
                </button>
                <button type="button" className="btn-mnc-discard" onClick={() => setHistoryStudent(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= FEE PLAN & HISTORY MODAL ================= */}
      {showFeeModal && (
        <div
          className="mnc-modal-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowFeeModal(false); }}
        >
          <div className="mnc-modal-window" role="dialog" aria-modal="true" style={{ width: "680px", height: "auto", maxHeight: "90vh" }}>
            <div className="mnc-modal-main w-100">
              <div className="mnc-modal-header">
                <div>
                  <h5 className="fw-800 mb-0">Fee Structure & History Ledger</h5>
                  <span className="text-muted x-small fw-600">
                    Student: {feeStudent?.name} ({feeStudent?.admission_number})
                  </span>
                </div>
                <button type="button" className="btn-close shadow-none" aria-label="Close" onClick={() => setShowFeeModal(false)}></button>
              </div>

              <div className="px-4 pt-3 pb-0 bg-light border-bottom d-flex gap-3">
                <button
                  type="button"
                  className={`btn btn-sm fw-700 pb-2 border-0 bg-transparent ${!isHistoryView ? "text-indigo border-bottom border-primary border-3" : "text-muted"}`}
                  onClick={() => setIsHistoryView(false)}
                >
                  {editingFeeRecord ? "Correcting Fee Entry" : "Active Plan"}
                </button>
                <button
                  type="button"
                  className={`btn btn-sm fw-700 pb-2 border-0 bg-transparent ${isHistoryView ? "text-indigo border-bottom border-primary border-3" : "text-muted"}`}
                  onClick={() => { setIsHistoryView(true); setEditingFeeRecord(null); }}
                >
                  History & Changes ({feeHistory.length})
                </button>
              </div>

              <div className="mnc-modal-scroller p-4">
                {feePlanLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary spinner-border-sm" role="status" aria-label="Loading"></div>
                  </div>
                ) : !isHistoryView ? (
                  <>
                    {editingFeeRecord && (
                      <div className="alert alert-warning py-2 px-3 mb-3 small d-flex justify-content-between align-items-center">
                        <div>
                          <strong>Correction mode:</strong> editing the entry effective from{" "}
                          <em>{toDateInput(editingFeeRecord.effective_from)}</em>.
                        </div>
                        <button type="button" className="btn btn-sm btn-outline-dark py-0" onClick={() => setEditingFeeRecord(null)}>
                          Cancel edit
                        </button>
                      </div>
                    )}

                    {!editingFeeRecord && activeFeePlan && (
                      <div className="alert alert-info py-2 px-3 mb-3 small d-flex justify-content-between align-items-center">
                        <div>
                          <strong>Active rate:</strong> ₹{activeFeePlan.amount} ({activeFeePlan.billing_cycle})
                          <div className="text-muted x-small">Effective since {toDateInput(activeFeePlan.effective_from)}</div>
                        </div>
                        <span className="badge bg-success">Active</span>
                      </div>
                    )}

                    <form onSubmit={handleFeeSubmit} className="row g-3">
                      <div className="col-12">
                        <label className="mnc-label">Fee Template (optional)</label>
                        <select name="fee_master_id" className="mnc-select" value={feeFormData.fee_master_id} onChange={handleFeeMasterChange}>
                          <option value="">-- Custom / manual amount --</option>
                          {feeMasters.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.fee_name} - ₹{m.amount} ({m.billing_cycle})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-6">
                        <label className="mnc-label">Base Fee Amount *</label>
                        <input type="number" min="0" step="0.01" name="amount" className="mnc-field" value={feeFormData.amount} onChange={handleFeeFieldChange("amount")} required />
                      </div>

                      <div className="col-md-6">
                        <label className="mnc-label">Billing Cycle *</label>
                        <select name="billing_cycle" className="mnc-select" value={feeFormData.billing_cycle} onChange={handleFeeFieldChange("billing_cycle")}>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="half_yearly">Half Yearly</option>
                          <option value="yearly">Yearly</option>
                        </select>
                      </div>

                      <div className="col-md-6">
                        <label className="mnc-label">Discount Amount</label>
                        <input type="number" min="0" step="0.01" name="discount" className="mnc-field" value={feeFormData.discount} onChange={handleFeeFieldChange("discount")} />
                      </div>

                      <div className="col-md-6">
                        <label className="mnc-label">Effective From *</label>
                        <input type="date" name="effective_from" className="mnc-field" value={feeFormData.effective_from} onChange={handleFeeFieldChange("effective_from")} required />
                        <small className="text-muted x-small">
                          {editingFeeRecord
                            ? "Updates the schedule of this record."
                            : "The previous period is archived and stays in history."}
                        </small>
                      </div>

                      <div className="col-12 mt-4 text-end">
                        <button type="button" className="btn-mnc-discard me-2" onClick={() => setShowFeeModal(false)}>Cancel</button>
                        <button type="submit" className="btn-mnc-sync" disabled={feeSubmitting}>
                          {feeSubmitting ? "Saving…" : editingFeeRecord ? "Save correction" : "Save & archive previous"}
                        </button>
                      </div>
                    </form>
                  </>
                ) : (
                  <div>
                    <div className="text-muted small mb-3 fw-600">
                      Chronological log of fee changes. Use the pencil icon to correct a wrongly entered value.
                    </div>
                    <div className="table-responsive border rounded-3">
                      <table className="table table-sm align-middle mb-0 bg-white">
                        <thead className="table-light text-uppercase x-small fw-800 text-muted">
                          <tr>
                            <th className="ps-3">Period</th>
                            <th>Amount</th>
                            <th>Discount</th>
                            <th>Cycle</th>
                            <th className="text-end pe-3">Action / Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {feeHistory.length === 0 ? (
                            <tr><td colSpan="5" className="text-center py-4 text-muted small">No fee records yet.</td></tr>
                          ) : (
                            feeHistory.map((item) => (
                              <tr key={item.id} className="small">
                                <td className="ps-3 fw-600">
                                  {toDateInput(item.effective_from)} <br />
                                  <span className="text-muted x-small">to {toDateInput(item.effective_to) || "Present"}</span>
                                </td>
                                <td className="fw-700 text-dark">₹{item.amount}</td>
                                <td>₹{item.discount}</td>
                                <td className="text-capitalize">{String(item.billing_cycle || "").replace("_", " ")}</td>
                                <td className="text-end pe-3">
                                  <div className="d-flex align-items-center justify-content-end gap-2">
                                    <span className={`badge ${item.status === "active" ? "bg-success" : "bg-secondary"}`}>
                                      {(item.status || "archived").toUpperCase()}
                                    </span>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary py-0 px-2"
                                      title="Correct this entry"
                                      aria-label="Correct this fee entry"
                                      onClick={() => handleEditFeeRecord(item)}
                                    >
                                      <i className="fas fa-pen x-small"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4 text-end">
                      <button type="button" className="btn-mnc-discard" onClick={() => setShowFeeModal(false)}>Close</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= ADD / EDIT STUDENT WIZARD ================= */}
      {showModal && (
        <div className="mnc-modal-overlay">
          <div className="mnc-modal-window" role="dialog" aria-modal="true">
            <div className="mnc-modal-side">
              <div className="p-4 border-bottom mnc-side-title">
                <h6 className="fw-800 text-dark mb-0">Enrollment</h6>
                <p className="x-small text-muted mb-0">Admission Wizard</p>
              </div>

              <div className="flex-grow-1 p-2 mnc-tab-scroller">
                <button type="button" className={`mnc-tab-link ${activeTab === "basic" ? "active" : ""}`} onClick={() => setActiveTab("basic")}>
                  <i className="fas fa-id-card"></i> Basic Profile
                </button>
                <button type="button" className={`mnc-tab-link ${activeTab === "family" ? "active" : ""}`} onClick={() => setActiveTab("family")}>
                  <i className="fas fa-users"></i> Family Info
                </button>
                <button type="button" className={`mnc-tab-link ${activeTab === "address" ? "active" : ""}`} onClick={() => setActiveTab("address")}>
                  <i className="fas fa-location-arrow"></i> Communication
                </button>
                <button type="button" className={`mnc-tab-link ${activeTab === "player" ? "active" : ""}`} onClick={() => setActiveTab("player")}>
                  <i className="fas fa-chess-knight"></i> Player Info
                </button>
                <button type="button" className={`mnc-tab-link ${activeTab === "tracking" ? "active" : ""}`} onClick={() => setActiveTab("tracking")}>
                  <i className="fas fa-history"></i> Tracking & Rejoin
                </button>
              </div>

              <div className="p-3 mt-auto mnc-side-status">
                <div className={`mnc-status-linear ${formData.status === "active" ? "is-active" : "is-inactive"}`}>
                  <div className="d-flex align-items-center flex-grow-1">
                    <div className="mnc-status-indicator"></div>
                    <span className="mnc-status-text">{formData.status?.toUpperCase()}</span>
                  </div>
                  <div className="form-check form-switch m-0 p-0">
                    <input
                      className="form-check-input mnc-switch"
                      type="checkbox"
                      name="status"
                      aria-label="Student active status"
                      checked={formData.status === "active"}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mnc-modal-main">
              <div className="mnc-modal-header">
                <div>
                  <h5 className="fw-800 mb-0">{isEditMode ? "Update Student Record" : "Add New Student"}</h5>
                  <span className="text-muted x-small fw-600">ID: {formData.admission_number || "PENDING"}</span>
                </div>
                <button type="button" className="btn-close shadow-none" aria-label="Close" onClick={() => setShowModal(false)}></button>
              </div>

              <div className="mnc-modal-scroller">
                <form className="row g-3" onSubmit={handleSubmit} noValidate>
                  {activeTab === "basic" && (
                    <>
                      <div className="col-md-6">
                        <label className="mnc-label">Admission Number *</label>
                        <input name="admission_number" disabled={isEditMode} className="mnc-field" value={formData.admission_number} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="mnc-label">Grade</label>
                        <input name="grade" className="mnc-field" value={formData.grade} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="mnc-label">First Name *</label>
                        <input name="name" className="mnc-field" value={formData.name} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="mnc-label">Last Name</label>
                        <input name="last_name" className="mnc-field" value={formData.last_name} onChange={handleChange} />
                      </div>
                      <div className="col-md-4">
                        <label className="mnc-label">DOB</label>
                        <input type="date" name="dob" className="mnc-field" value={formData.dob || ""} onChange={handleChange} max={todayISO()} />
                      </div>
                      <div className="col-md-4">
                        <label className="mnc-label">Gender</label>
                        <select name="gender" className="mnc-select" value={formData.gender} onChange={handleChange}>
                          <option value="M">Male</option>
                          <option value="F">Female</option>
                          <option value="O">Other</option>
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="mnc-label">Mother Tongue</label>
                        <input name="mother_tongue" className="mnc-field" value={formData.mother_tongue} onChange={handleChange} />
                      </div>
                      <div className="col-12">
                        <label className="mnc-label">Aadhaar Number</label>
                        <input name="aadhaar_no" inputMode="numeric" maxLength={12} className="mnc-field" value={formData.aadhaar_no} onChange={handleChange} />
                      </div>
                    </>
                  )}

                  {activeTab === "family" && (
                    <>
                      <div className="col-md-6"><label className="mnc-label">Father's Name</label><input name="father_name" className="mnc-field" value={formData.father_name} onChange={handleChange} /></div>
                      <div className="col-md-6"><label className="mnc-label">Father's Occupation</label><input name="father_occupation" className="mnc-field" value={formData.father_occupation} onChange={handleChange} /></div>
                      <div className="col-md-6"><label className="mnc-label">Mother's Name</label><input name="mother_name" className="mnc-field" value={formData.mother_name} onChange={handleChange} /></div>
                      <div className="col-md-6"><label className="mnc-label">Mother's Occupation</label><input name="mother_occupation" className="mnc-field" value={formData.mother_occupation} onChange={handleChange} /></div>
                      <div className="col-md-12"><label className="mnc-label">Guardian Name</label><input name="guardian_name" className="mnc-field" value={formData.guardian_name} onChange={handleChange} /></div>
                      <div className="col-md-12"><label className="mnc-label">School Last Attended</label><input name="school" className="mnc-field" value={formData.school} onChange={handleChange} /></div>
                    </>
                  )}

                  {activeTab === "address" && (
                    <>
                      <div className="col-md-6"><label className="mnc-label">Mobile Number *</label><input name="mobile_no" inputMode="tel" className="mnc-field" value={formData.mobile_no} onChange={handleChange} /></div>
                      <div className="col-md-6"><label className="mnc-label">Alternative No</label><input name="alternative_no" inputMode="tel" className="mnc-field" value={formData.alternative_no} onChange={handleChange} /></div>
                      <div className="col-md-12"><label className="mnc-label">Email Address</label><input type="email" name="email" className="mnc-field" value={formData.email} onChange={handleChange} /></div>
                      <div className="col-md-6"><label className="mnc-label">Address 1</label><input name="address" className="mnc-field" value={formData.address} onChange={handleChange} /></div>
                      <div className="col-md-6"><label className="mnc-label">Address 2</label><input name="address2" className="mnc-field" value={formData.address2} onChange={handleChange} /></div>
                      <div className="col-md-4">
                        <label className="mnc-label">State</label>
                        <select name="state" className="mnc-select" value={formData.state} onChange={handleChange}>
                          <option value="">Select State</option>
                          {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="mnc-label">District</label>
                        <select name="district" className="mnc-select" value={formData.district} onChange={handleChange} disabled={!formData.state}>
                          <option value="">Select District</option>
                          {(DISTRICTS_MAP[formData.state] || []).map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4"><label className="mnc-label">Pincode</label><input name="pincode" inputMode="numeric" maxLength={6} className="mnc-field" value={formData.pincode} onChange={handleChange} /></div>
                    </>
                  )}

                  {activeTab === "player" && (
                    <>
                      <div className="col-12">
                        <div className="mnc-section-note">
                          <i className="fas fa-info-circle me-2"></i>
                          Federation and online-play identifiers used for tournament registration and ratings.
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="mnc-label">
                          {dcaLabel} {!formData.district && <span className="mnc-label-hint">(select district in Communication tab)</span>}
                        </label>
                        <input name="dca_id" className="mnc-field" value={formData.dca_id} onChange={handleChange} placeholder="e.g. CHN-0451" />
                      </div>
                      <div className="col-md-6">
                        <label className="mnc-label">TNSCA ID</label>
                        <input name="tnsca_id" className="mnc-field" value={formData.tnsca_id} onChange={handleChange} placeholder="Tamil Nadu State Chess Assoc. ID" />
                      </div>
                      <div className="col-md-6">
                        <label className="mnc-label">AICF ID</label>
                        <input name="aicf_id" className="mnc-field" value={formData.aicf_id} onChange={handleChange} placeholder="All India Chess Federation ID" />
                      </div>
                      <div className="col-md-6">
                        <label className="mnc-label">FIDE ID</label>
                        <input name="fide_id" className="mnc-field" value={formData.fide_id} onChange={handleChange} placeholder="FIDE registration number" />
                      </div>
                      <div className="col-md-6">
                        <label className="mnc-label">Lichess ID</label>
                        <input name="lichess_id" className="mnc-field" value={formData.lichess_id} onChange={handleChange} placeholder="lichess.org username" />
                      </div>
                      <div className="col-md-6">
                        <label className="mnc-label">Lichess Password</label>
                        <div className="mnc-input-group">
                          <input
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            name="lichess_password"
                            className="mnc-field"
                            value={formData.lichess_password}
                            onChange={handleChange}
                            placeholder="••••••••"
                          />
                          <button
                            type="button"
                            className="mnc-eye-btn"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            onClick={() => setShowPassword((v) => !v)}
                          >
                            <i className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {activeTab === "tracking" && (
                    <>
                      <div className="col-12">
                        <div className="mnc-track-banner">
                          <div className="mnc-track-banner-icon"><i className="fas fa-user-clock"></i></div>
                          <div className="mnc-track-banner-text">
                            <div className="mnc-track-banner-title">Enrollment Timeline</div>
                            <div className="mnc-track-banner-sub">
                              Track admissions, leaves and re-join sessions mapped to the fee ledger.
                            </div>
                          </div>
                          <button type="button" className="mnc-btn-add-period" onClick={handleAddNewEnrollmentRow}>
                            <i className="fas fa-plus"></i> Add Period
                          </button>
                        </div>
                      </div>

                      <div className="col-12">
                        {!formData.enrollments || formData.enrollments.length === 0 ? (
                          <div className="mnc-track-empty">
                            <i className="fas fa-calendar-plus"></i>
                            <div className="fw-700">No enrollment periods recorded</div>
                            <div className="x-small">Click "Add Period" to start tracking membership.</div>
                          </div>
                        ) : (
                          <div className="mnc-timeline">
                            {formData.enrollments.map((en, index) => {
                              const meta = getEnrollmentMeta(en);
                              return (
                                <div key={en.id ?? `new-${index}`} className={`mnc-period-card is-${meta.status}`}>
                                  <span className="mnc-period-dot"></span>

                                  <div className="mnc-period-head">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                      <span className="mnc-period-index">Period {index + 1}</span>
                                      <span className={`mnc-period-badge ${meta.status}`}>{meta.label}</span>
                                      <span className="mnc-period-duration">
                                        <i className="far fa-clock me-1"></i>{meta.duration}
                                      </span>
                                    </div>
                                    <button
                                      type="button"
                                      className="mnc-period-remove"
                                      title="Remove period"
                                      aria-label={`Remove period ${index + 1}`}
                                      onClick={() => handleRemoveEnrollmentRow(index)}
                                    >
                                      <i className="fas fa-trash-alt"></i>
                                    </button>
                                  </div>

                                  <div className="mnc-period-grid">
                                    <div className="mnc-period-cell">
                                      <label className="mnc-label">Date of Joining *</label>
                                      <input
                                        type="date"
                                        className="mnc-field"
                                        value={en.doj || ""}
                                        onChange={(e) => handleEnrollmentChange(index, "doj", e.target.value)}
                                        required
                                      />
                                    </div>
                                    <div className="mnc-period-cell">
                                      <label className="mnc-label">Leaving Date</label>
                                      <input
                                        type="date"
                                        className="mnc-field"
                                        value={en.leaving_date || ""}
                                        min={en.doj || undefined}
                                        onChange={(e) => handleEnrollmentChange(index, "leaving_date", e.target.value)}
                                      />
                                    </div>
                                    <div className="mnc-period-cell">
                                      <label className="mnc-label">Remarks</label>
                                      <input
                                        type="text"
                                        className="mnc-field"
                                        placeholder="e.g. Rejoined after summer break"
                                        value={en.remarks || ""}
                                        onChange={(e) => handleEnrollmentChange(index, "remarks", e.target.value)}
                                      />
                                    </div>
                                  </div>

                                  <div className="mnc-period-hint">
                                    <i className="fas fa-info-circle me-1"></i>
                                    Leave the leaving date blank if the student is currently active.
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </form>
              </div>

              <div className="mnc-modal-footer">
                <button type="button" className="btn-mnc-discard" onClick={() => setShowModal(false)}>Discard</button>
                <button type="button" className="btn-mnc-sync" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? "Saving…" : isEditMode ? "Save changes" : "Confirm admission"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .mnc-page-bg { background: #f8fafc; min-height: 100vh; font-family: 'Plus Jakarta Sans', sans-serif; }
        .fw-800 { font-weight: 800; }
        .x-small { font-size: 0.72rem; }
        .uppercase { text-transform: uppercase; letter-spacing: 0.05em; }
        .text-indigo { color: #6366f1 !important; }

        .mnc-page-bg button:focus-visible,
        .mnc-page-bg select:focus-visible,
        .mnc-page-bg input:focus-visible { outline: 2px solid #6366f1; outline-offset: 2px; }

        .btn-mnc-primary { background: #0f172a; color: white; border-radius: 12px; padding: 10px 22px; font-weight: 700; border: none; font-size: 0.85rem; white-space: nowrap; transition: 0.2s; cursor: pointer; }
        .btn-mnc-primary:hover { background: #1e293b; color: white; }
        .btn-mnc-sync { background: #6366f1; color: white; border-radius: 10px; padding: 8px 24px; font-weight: 700; border: none; font-size: 0.85rem; cursor: pointer; transition: 0.2s; }
        .btn-mnc-sync:hover:not(:disabled) { background: #4f46e5; }
        .btn-mnc-sync:disabled { opacity: 0.6; cursor: not-allowed; }
        .btn-mnc-discard { background: #fff; color: #64748b; border-radius: 10px; padding: 8px 24px; font-weight: 700; border: 1px solid #e2e8f0; font-size: 0.85rem; cursor: pointer; }
        .btn-mnc-outline { background: transparent; border: 1px solid #e2e8f0; padding: 6px 14px; border-radius: 8px; font-weight: 700; font-size: 0.75rem; color: #6366f1; transition: 0.2s; cursor: pointer; }
        .btn-mnc-outline:hover { background: #6366f1; color: white; border-color: #6366f1; }
        .btn-mnc-outline-fee { background: transparent; border: 1px solid #e2e8f0; padding: 6px 10px; border-radius: 8px; font-weight: 700; font-size: 0.75rem; color: #10b981; transition: 0.2s; cursor: pointer; }
        .btn-mnc-outline-fee:hover { background: #10b981; color: white; border-color: #10b981; }
        .btn-mnc-outline-view { background: transparent; border: 1px solid #e2e8f0; padding: 6px 10px; border-radius: 8px; font-weight: 700; font-size: 0.75rem; color: #0ea5e9; transition: 0.2s; cursor: pointer; }
        .btn-mnc-outline-view:hover { background: #0ea5e9; color: white; border-color: #0ea5e9; }

        .badge-total { background: #e0e7ff; color: #4338ca; padding: 5px 14px; border-radius: 100px; font-weight: 800; font-size: 0.72rem; }

        .mnc-card { background: white; border-radius: 20px; border: 1px solid #e2e8f0; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02); }
        .mnc-thead th { background: #f8fafc; color: #64748b; font-size: 0.65rem; font-weight: 800; padding: 16px; text-transform: uppercase; letter-spacing: 0.1em; border-bottom: 1px solid #e2e8f0; }
        .mnc-row { border-bottom: 1px solid #f1f5f9; transition: 0.15s ease; }
        .mnc-row:hover { background: #f8fafc; }
        .mnc-avatar-mini { width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-weight: 800; color: white; flex-shrink: 0; }
        .mnc-avatar-mini.active { background: #10b981; }
        .mnc-avatar-mini.inactive { background: #f43f5e; }
        .mnc-avatar-img { width: 38px; height: 38px; border-radius: 10px; object-fit: cover; flex-shrink: 0; }

        .mnc-avatar-lg { width: 56px; height: 56px; border-radius: 14px; object-fit: cover; flex-shrink: 0; }
        .mnc-avatar-lg-fallback { width: 56px; height: 56px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-weight: 800; color: white; font-size: 1.25rem; flex-shrink: 0; }
        .mnc-avatar-lg-fallback.active { background: #10b981; }
        .mnc-avatar-lg-fallback.inactive { background: #f43f5e; }

        /* ===== QUICK VIEW ===== */
        .mnc-view-window { width: 720px; height: auto; max-height: 90vh; }
        .mnc-view-hero { position: relative; display: flex; align-items: center; gap: 18px; padding: 26px 28px; background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); color: #fff; }
        .mnc-view-close { position: absolute; top: 14px; right: 14px; width: 32px; height: 32px; border-radius: 50%; border: none; background: rgba(255,255,255,0.1); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 0.8rem; cursor: pointer; transition: 0.2s; }
        .mnc-view-close:hover { background: rgba(255,255,255,0.22); }
        .mnc-view-avatar { width: 68px; height: 68px; border-radius: 18px; object-fit: cover; flex-shrink: 0; border: 2px solid rgba(255,255,255,0.18); }
        .mnc-view-avatar.fallback { display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 1.6rem; color: #fff; }
        .mnc-view-avatar.fallback.active { background: #10b981; }
        .mnc-view-avatar.fallback.inactive { background: #f43f5e; }
        .mnc-view-hero-text { min-width: 0; padding-right: 30px; }
        .mnc-view-name { font-weight: 800; font-size: 1.2rem; margin: 0 0 10px; color: #fff; overflow-wrap: anywhere; }
        .mnc-view-chips { display: flex; flex-wrap: wrap; gap: 8px; }
        .mnc-view-chip { background: rgba(255,255,255,0.12); color: #e2e8f0; font-size: 0.72rem; font-weight: 700; padding: 4px 12px; border-radius: 100px; }
        .mnc-view-chip.mono { font-family: 'Courier New', Courier, monospace; letter-spacing: 0.02em; }
        .mnc-view-chip.status.active { background: rgba(16,185,129,0.22); color: #6ee7b7; }
        .mnc-view-chip.status.inactive { background: rgba(244,63,94,0.22); color: #fda4af; }

        .mnc-view-body { padding: 22px 28px; background: #f8fafc; min-height: 340px; }
        .mnc-view-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .mnc-view-tile { position: relative; display: flex; align-items: flex-start; gap: 12px; padding: 14px 16px; background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; transition: 0.15s; min-width: 0; }
        .mnc-view-tile:hover { border-color: #c7d2fe; box-shadow: 0 6px 16px -8px rgba(99,102,241,0.25); }
        .mnc-view-tile.wide { grid-column: 1 / -1; }
        .mnc-view-icon { width: 34px; height: 34px; border-radius: 10px; background: #eef2ff; color: #6366f1; display: flex; align-items: center; justify-content: center; font-size: 0.8rem; flex-shrink: 0; }
        .mnc-view-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
        .mnc-view-label { font-size: 0.68rem; font-weight: 700; color: #64748b; }
        .mnc-view-value { font-size: 0.88rem; font-weight: 700; color: #0f172a; overflow-wrap: anywhere; line-height: 1.4; }
        .mnc-view-value.is-empty { color: #94a3b8; font-weight: 600; font-style: italic; }
        .mnc-view-copy { width: 30px; height: 30px; border-radius: 8px; border: 1px solid #e2e8f0; background: #fff; color: #64748b; display: flex; align-items: center; justify-content: center; font-size: 0.78rem; cursor: pointer; flex-shrink: 0; opacity: 0; transition: 0.15s; }
        .mnc-view-tile:hover .mnc-view-copy, .mnc-view-copy:focus-visible { opacity: 1; }
        .mnc-view-copy:hover { background: #6366f1; color: #fff; border-color: #6366f1; }

        .mnc-view-footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 16px 28px; background: #fff; border-top: 1px solid #f1f5f9; }
        .mnc-view-copyall { background: none; border: none; color: #475569; font-size: 0.8rem; font-weight: 700; cursor: pointer; padding: 8px 4px; }
        .mnc-view-copyall:hover { color: #6366f1; }
        .mnc-view-actions { display: flex; gap: 10px; }
        .btn-mnc-outline-fee-text { background: #fff; border: 1px solid #a7f3d0; color: #059669; border-radius: 10px; padding: 8px 18px; font-weight: 700; font-size: 0.85rem; cursor: pointer; transition: 0.2s; }
        .btn-mnc-outline-fee-text:hover { background: #10b981; color: #fff; border-color: #10b981; }

        .mnc-status-pill { padding: 4px 12px; border-radius: 100px; font-size: 0.6rem; font-weight: 800; }
        .mnc-status-pill.active { background: #dcfce7; color: #15803d; }
        .mnc-status-pill.inactive { background: #fee2e2; color: #b91c1c; }
        .mnc-id-tag { background: #f1f5f9; padding: 4px 8px; border-radius: 6px; font-weight: 700; font-size: 0.75rem; color: #475569; font-family: 'Courier New', Courier, monospace; }

        .mnc-footer-pagination { padding: 16px 24px; display: flex; justify-content: space-between; align-items: center; background: #fff; border-top: 1px solid #f1f5f9; flex-wrap: wrap; gap: 10px; }
        .mnc-pagination-hub { display: flex; gap: 5px; align-items: center; flex-wrap: wrap; }
        .mnc-page-num { min-width: 32px; height: 32px; padding: 0 6px; border: 1px solid #e2e8f0; background: white; border-radius: 8px; font-size: 0.75rem; font-weight: 700; color: #64748b; transition: 0.2s; cursor: pointer; }
        .mnc-page-num:hover { border-color: #cbd5e1; color: #0f172a; }
        .mnc-page-num.active { background: #0f172a; border-color: #0f172a; color: white; }
        .mnc-page-gap { color: #94a3b8; font-weight: 700; padding: 0 4px; }
        .mnc-page-arrow { background: white; border: 1px solid #e2e8f0; width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #64748b; cursor: pointer; }
        .mnc-page-arrow:disabled { opacity: 0.3; cursor: not-allowed; }

        /* TOOLBAR */
        .mnc-toolbar { display: flex; gap: 16px; background: white; padding: 12px 16px; border-radius: 16px; border: 1px solid #e2e8f0; align-items: center; }
        .mnc-search-group { flex: 1; position: relative; display: flex; align-items: center; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 0 14px; height: 44px; transition: 0.2s; }
        .mnc-search-group:focus-within { border-color: #6366f1; background: white; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }
        .mnc-search-icon { color: #94a3b8; font-size: 0.85rem; margin-right: 12px; }
        .mnc-search-input { border: none; outline: none !important; width: 100%; font-weight: 600; font-size: 0.85rem; background: transparent; color: #1e293b; }
        .mnc-clear-search { background: none; border: none; color: #94a3b8; font-size: 0.8rem; cursor: pointer; padding: 4px; }
        .mnc-clear-search:hover { color: #0f172a; }

        .mnc-filter-group { display: flex; align-items: center; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 0 14px; height: 44px; gap: 10px; min-width: 210px; }
        .mnc-filter-label-wrap { display: flex; align-items: center; gap: 6px; border-right: 1px solid #e2e8f0; padding-right: 12px; }
        .mnc-filter-caption { font-size: 0.7rem; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; }
        .mnc-toolbar-select { border: none; outline: none; background: transparent; font-size: 0.82rem; font-weight: 700; color: #334155; width: 100%; cursor: pointer; }

        /* MODAL */
        .mnc-modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.5); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 16px; }
        .mnc-modal-window { width: 980px; max-width: 100%; height: 650px; max-height: 92vh; background: white; border-radius: 24px; display: flex; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); }
        .mnc-modal-side { width: 260px; flex-shrink: 0; background: #f8fafc; border-right: 1px solid #e2e8f0; display: flex; flex-direction: column; }
        .mnc-modal-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
        .mnc-modal-header { padding: 20px 30px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; gap: 10px; }
        .mnc-modal-scroller { flex: 1; overflow-y: auto; padding: 30px; }
        .mnc-modal-footer { padding: 20px 30px; border-top: 1px solid #f1f5f9; display: flex; justify-content: flex-end; gap: 12px; }

        .mnc-section-note { background: #eef2ff; border: 1px solid #c7d2fe; color: #4338ca; font-size: 0.78rem; font-weight: 600; padding: 10px 14px; border-radius: 10px; margin-bottom: 4px; }
        .mnc-label-hint { text-transform: none; font-weight: 500; color: #94a3b8; font-size: 0.62rem; letter-spacing: 0; margin-left: 4px; }

        .mnc-status-linear { display: flex; align-items: center; justify-content: space-between; padding: 8px 14px; border-radius: 100px; border: 1px solid transparent; }
        .mnc-status-linear.is-active { background: #f0fdf4; border-color: #bbf7d0; color: #166534; }
        .mnc-status-linear.is-inactive { background: #fff1f2; border-color: #fecdd3; color: #9f1239; }
        .mnc-status-indicator { width: 6px; height: 6px; border-radius: 50%; background: currentColor; margin-right: 10px; box-shadow: 0 0 8px currentColor; }
        .mnc-status-text { font-size: 0.65rem; font-weight: 800; letter-spacing: 0.05em; }

        .mnc-switch { cursor: pointer; width: 32px !important; height: 18px !important; margin-top: 0 !important; }
        .mnc-switch:checked { background-color: #10b981 !important; border-color: #10b981 !important; }

        .mnc-tab-link { border: none; background: none; text-align: left; padding: 12px 20px; width: 100%; border-radius: 10px; font-weight: 700; color: #64748b; font-size: 0.82rem; display: flex; align-items: center; gap: 12px; margin-bottom: 4px; white-space: nowrap; cursor: pointer; }
        .mnc-tab-link.active { background: white; color: #6366f1; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05); }
        .mnc-label { font-size: 0.65rem; font-weight: 800; color: #64748b; text-transform: uppercase; margin-bottom: 6px; display: block; }
        .mnc-field, .mnc-select { width: 100%; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 14px; font-size: 0.85rem; font-weight: 600; color: #1e293b; outline: none; background: #fff; }
        .mnc-field:focus, .mnc-select:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }
        .mnc-field:disabled, .mnc-select:disabled { background: #f8fafc; color: #94a3b8; cursor: not-allowed; }

        .mnc-input-group { position: relative; display: flex; align-items: center; width: 100%; }
        .mnc-input-group .mnc-field { padding-right: 44px; }
        .mnc-eye-btn { position: absolute; right: 12px; background: transparent; border: none; color: #64748b; font-size: 0.85rem; cursor: pointer; padding: 4px; }
        .mnc-eye-btn:hover { color: #6366f1; }

        /* ===== TRACKING & REJOIN ===== */
        .mnc-track-banner { display: flex; align-items: center; gap: 14px; padding: 14px 18px; border-radius: 14px; background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); color: #fff; }
        .mnc-track-banner-icon { width: 40px; height: 40px; border-radius: 12px; background: rgba(255,255,255,0.1); display: flex; align-items: center; justify-content: center; font-size: 1rem; flex-shrink: 0; }
        .mnc-track-banner-text { flex: 1; min-width: 0; }
        .mnc-track-banner-title { font-weight: 800; font-size: 0.9rem; }
        .mnc-track-banner-sub { font-size: 0.72rem; color: #cbd5e1; font-weight: 500; margin-top: 2px; }
        .mnc-btn-add-period { background: #6366f1; color: #fff; border: none; border-radius: 10px; padding: 9px 16px; font-size: 0.78rem; font-weight: 700; display: inline-flex; align-items: center; gap: 8px; cursor: pointer; white-space: nowrap; transition: 0.2s; }
        .mnc-btn-add-period:hover { background: #4f46e5; transform: translateY(-1px); }

        .mnc-track-empty { text-align: center; padding: 40px 20px; border: 2px dashed #e2e8f0; border-radius: 16px; background: #f8fafc; color: #64748b; }
        .mnc-track-empty i { font-size: 1.8rem; color: #94a3b8; margin-bottom: 10px; display: block; }

        .mnc-timeline { position: relative; display: flex; flex-direction: column; gap: 16px; padding-left: 26px; }
        .mnc-timeline::before { content: ""; position: absolute; left: 7px; top: 8px; bottom: 8px; width: 2px; background: #e2e8f0; border-radius: 2px; }

        .mnc-period-card { position: relative; background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 16px 18px; box-shadow: 0 1px 2px rgba(15,23,42,0.04); transition: 0.2s; }
        .mnc-period-card:hover { box-shadow: 0 8px 20px -8px rgba(15,23,42,0.12); border-color: #cbd5e1; }
        .mnc-period-card.is-active { border-left: 4px solid #10b981; }
        .mnc-period-card.is-completed { border-left: 4px solid #94a3b8; }
        .mnc-period-card.is-draft { border-left: 4px solid #f59e0b; }
        .mnc-period-dot { position: absolute; left: -26px; top: 22px; width: 16px; height: 16px; border-radius: 50%; background: #fff; border: 3px solid #cbd5e1; z-index: 1; }
        .mnc-period-card.is-active .mnc-period-dot { border-color: #10b981; box-shadow: 0 0 0 4px rgba(16,185,129,0.15); }
        .mnc-period-card.is-draft .mnc-period-dot { border-color: #f59e0b; }

        .mnc-period-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding-bottom: 12px; margin-bottom: 14px; border-bottom: 1px dashed #e2e8f0; }
        .mnc-period-index { font-size: 0.8rem; font-weight: 800; color: #0f172a; }
        .mnc-period-badge { font-size: 0.6rem; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; padding: 3px 10px; border-radius: 100px; }
        .mnc-period-badge.active { background: #dcfce7; color: #15803d; }
        .mnc-period-badge.completed { background: #f1f5f9; color: #475569; }
        .mnc-period-badge.draft { background: #fef3c7; color: #b45309; }
        .mnc-period-duration { font-size: 0.7rem; font-weight: 700; color: #64748b; }
        .mnc-period-remove { width: 32px; height: 32px; border-radius: 8px; border: 1px solid #fee2e2; background: #fff; color: #ef4444; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; cursor: pointer; transition: 0.2s; flex-shrink: 0; }
        .mnc-period-remove:hover { background: #ef4444; color: #fff; border-color: #ef4444; }

        .mnc-period-grid { display: grid; grid-template-columns: 1fr 1fr 1.4fr; gap: 14px; align-items: end; }
        .mnc-period-cell { min-width: 0; }
        .mnc-period-cell .mnc-field { height: 42px; }
        .mnc-period-hint { margin-top: 12px; font-size: 0.7rem; color: #94a3b8; font-weight: 600; }

        /* ===== LIST: ENROLLMENT COLUMN ===== */
        .mnc-enr-cell { display: flex; flex-direction: column; gap: 4px; min-width: 190px; }
        .mnc-enr-row { display: flex; align-items: center; gap: 10px; font-size: 0.74rem; line-height: 1.3; }
        .mnc-enr-key { width: 50px; flex-shrink: 0; font-size: 0.6rem; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: #94a3b8; }
        .mnc-enr-val { font-weight: 700; color: #334155; }
        .mnc-enr-val.is-active { color: #15803d; display: inline-flex; align-items: center; gap: 6px; }
        .mnc-enr-val.is-active::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,0.18); }
        .mnc-enr-val.is-left { color: #b45309; }
        .mnc-enr-val.muted { color: #cbd5e1; font-weight: 600; }
        .mnc-enr-rejoin { background: #eef2ff; color: #4338ca; border-radius: 100px; padding: 1px 8px; font-size: 0.66rem; font-weight: 800; margin-right: 6px; }
        .mnc-enr-row { flex-wrap: wrap; }
        .mnc-enr-prev { font-size: 0.66rem; font-weight: 600; color: #94a3b8; }
        .mnc-enr-history { align-self: flex-start; margin-top: 4px; background: none; border: none; padding: 2px 0; color: #6366f1; font-size: 0.72rem; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
        .mnc-enr-history:hover { color: #4338ca; text-decoration: underline; }
        .mnc-enr-none { color: #94a3b8; font-size: 0.74rem; font-style: italic; font-weight: 600; }

        /* ===== VIEW: TABS, STATS, TIMELINE ===== */
        .mnc-view-tabs { display: flex; gap: 4px; padding: 0 20px; background: #fff; border-bottom: 1px solid #e2e8f0; overflow-x: auto; }
        .mnc-view-tabs::-webkit-scrollbar { display: none; }
        .mnc-view-tab { border: none; background: none; padding: 14px 14px; font-size: 0.8rem; font-weight: 700; color: #64748b; display: inline-flex; align-items: center; gap: 8px; border-bottom: 3px solid transparent; margin-bottom: -1px; cursor: pointer; white-space: nowrap; transition: 0.15s; }
        .mnc-view-tab:hover { color: #0f172a; }
        .mnc-view-tab.active { color: #6366f1; border-bottom-color: #6366f1; }
        .mnc-view-tab-count { background: #eef2ff; color: #4338ca; border-radius: 100px; font-size: 0.65rem; font-weight: 800; padding: 1px 8px; }

        .mnc-view-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 14px; }
        .mnc-view-stat { display: flex; flex-direction: column; gap: 3px; min-width: 0; background: #fff; border: 1px solid #e2e8f0; border-top: 3px solid #6366f1; border-radius: 14px; padding: 12px 14px; }
        .mnc-view-stat.left { border-top-color: #f59e0b; }
        .mnc-view-stat.left.is-active { border-top-color: #10b981; }
        .mnc-view-stat.rejoin { border-top-color: #0ea5e9; }
        .mnc-view-stat.tenure { border-top-color: #0f172a; }
        .mnc-view-stat-label { font-size: 0.62rem; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: #94a3b8; }
        .mnc-view-stat-value { font-size: 0.92rem; font-weight: 800; color: #0f172a; overflow-wrap: anywhere; }
        .mnc-view-stat.left.is-active .mnc-view-stat-value { color: #15803d; }
        .mnc-view-stat-sub { font-size: 0.68rem; font-weight: 600; color: #64748b; }

        .mnc-vt { position: relative; display: flex; flex-direction: column; gap: 12px; padding-left: 24px; }
        .mnc-vt::before { content: ""; position: absolute; left: 6px; top: 8px; bottom: 8px; width: 2px; background: #e2e8f0; border-radius: 2px; }
        .mnc-vt-item { position: relative; background: #fff; border: 1px solid #e2e8f0; border-left: 4px solid #94a3b8; border-radius: 14px; padding: 14px 16px; }
        .mnc-vt-item.is-active { border-left-color: #10b981; }
        .mnc-vt-item.is-draft { border-left-color: #f59e0b; }
        .mnc-vt-dot { position: absolute; left: -24px; top: 18px; width: 14px; height: 14px; border-radius: 50%; background: #fff; border: 3px solid #cbd5e1; }
        .mnc-vt-item.is-active .mnc-vt-dot { border-color: #10b981; box-shadow: 0 0 0 4px rgba(16,185,129,0.15); }
        .mnc-vt-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
        .mnc-vt-dates { display: flex; align-items: center; gap: 10px; margin-top: 10px; font-size: 0.9rem; font-weight: 800; color: #0f172a; flex-wrap: wrap; }
        .mnc-vt-arrow { color: #94a3b8; font-size: 0.8rem; }
        .mnc-vt-remarks { margin-top: 8px; font-size: 0.78rem; font-weight: 600; color: #64748b; }

        /* ===== HISTORY POPUP ===== */
        .mnc-hist-window { width: 520px; height: auto; max-height: 90vh; }
        .mnc-hist-head { position: relative; padding: 22px 26px; background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); color: #fff; }
        .mnc-hist-title { font-size: 1.05rem; font-weight: 800; padding-right: 36px; }
        .mnc-hist-sub { margin-top: 4px; font-size: 0.78rem; font-weight: 600; color: #cbd5e1; overflow-wrap: anywhere; padding-right: 36px; }
        .mnc-hist-summary { display: flex; flex-wrap: wrap; gap: 8px; padding: 14px 26px; background: #fff; border-bottom: 1px solid #e2e8f0; }
        .mnc-hist-chip { font-size: 0.72rem; font-weight: 700; background: #f1f5f9; color: #334155; border-radius: 100px; padding: 5px 12px; }
        .mnc-hist-chip.active { background: #dcfce7; color: #15803d; }
        .mnc-hist-chip.rejoin { background: #eef2ff; color: #4338ca; }
        .mnc-hist-body { padding: 22px 26px; background: #f8fafc; }
        .mnc-vt-gap { align-self: flex-start; font-size: 0.68rem; font-weight: 700; color: #b45309; background: #fffbeb; border: 1px dashed #fcd34d; border-radius: 100px; padding: 3px 12px; }

        @media (prefers-reduced-motion: reduce) {
          .mnc-page-bg * { transition: none !important; }
        }

        /* RESPONSIVE */
        @media (max-width: 992px) {
          .mnc-modal-window { width: 100%; height: 92vh; }
        }

        @media (max-width: 768px) {
          .container-fluid.px-4 { padding-left: 14px !important; padding-right: 14px !important; }
          .mnc-header-row { flex-direction: column; align-items: stretch !important; gap: 14px; }
          .mnc-header-row .btn-mnc-primary { width: 100%; }

          .mnc-toolbar { flex-direction: column; gap: 12px; padding: 14px; align-items: stretch; }
          .mnc-filter-group { width: 100%; }

          .mnc-modal-overlay { padding: 0; align-items: flex-end; }
          .mnc-modal-window { flex-direction: column; width: 100% !important; height: 94vh; max-height: 94vh; border-radius: 20px 20px 0 0; }
          .mnc-modal-side { width: 100%; flex-direction: row; align-items: center; border-right: none; border-bottom: 1px solid #e2e8f0; }
          .mnc-side-title { display: none; }
          .mnc-side-status { margin-top: 0 !important; padding: 10px 14px !important; }
          .mnc-tab-scroller { display: flex; overflow-x: auto; padding: 10px !important; gap: 6px; flex: 1; }
          .mnc-tab-scroller::-webkit-scrollbar { display: none; }
          .mnc-tab-link { width: auto; margin-bottom: 0; padding: 10px 14px; font-size: 0.72rem; }

          .mnc-modal-header { padding: 16px 18px; }
          .mnc-modal-scroller { padding: 18px; }
          .mnc-modal-footer { padding: 14px 18px; flex-wrap: wrap; }
          .mnc-modal-footer button { flex: 1; }
          .mnc-footer-pagination { flex-direction: column; align-items: flex-start; }

          .mnc-view-window { height: auto; max-height: 92vh; }
          .mnc-view-hero { padding: 22px 18px; gap: 14px; }
          .mnc-view-avatar { width: 56px; height: 56px; border-radius: 14px; }
          .mnc-view-body { padding: 16px 18px; }
          .mnc-view-grid { grid-template-columns: 1fr; }
          .mnc-view-copy { opacity: 1; }
          .mnc-view-footer { padding: 14px 18px; flex-direction: column; align-items: stretch; }
          .mnc-view-copyall { text-align: center; }
          .mnc-view-actions { flex-direction: column; }
          .mnc-view-actions button { width: 100%; }
          .mnc-view-stats { grid-template-columns: 1fr 1fr; }
          .mnc-view-tabs { padding: 0 10px; }
          .mnc-view-body { min-height: 0; }
          .mnc-hist-window { height: auto; max-height: 92vh; }
          .mnc-hist-head { padding: 20px 18px; }
          .mnc-hist-summary { padding: 12px 18px; }
          .mnc-hist-body { padding: 18px; }

          .mnc-track-banner { flex-wrap: wrap; }
          .mnc-btn-add-period { width: 100%; justify-content: center; }
          .mnc-period-grid { grid-template-columns: 1fr; }
          .mnc-timeline { padding-left: 22px; }
          .mnc-period-dot { left: -22px; }
        }

        @media (max-width: 576px) {
          .mnc-table thead { display: none; }
          .mnc-table, .mnc-table tbody, .mnc-table tr, .mnc-table td { display: block; width: 100%; }
          .mnc-row { border: 1px solid #f1f5f9; border-radius: 14px; margin-bottom: 12px; padding: 6px 0; }
          .mnc-table td { padding: 8px 16px !important; border: none !important; text-align: left !important; }
          .mnc-table td[data-label]::before {
            content: attr(data-label);
            display: block;
            font-size: 0.6rem;
            font-weight: 800;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: #94a3b8;
            margin-bottom: 4px;
          }
          .mnc-table td.text-end { text-align: left !important; }
          .mnc-table td.text-end > div { justify-content: flex-start !important; }
          .mnc-modal-window { border-radius: 16px 16px 0 0; }
          .mnc-tab-link { padding: 10px 12px; }
        }
      `}</style>
    </div>
  );
};

export default Students;
